// A deal's verbs outside the board (spec 09 §4.14's Deal bullet, §4.15's deal
// peek): Move, Mark won and Mark lost on an open deal, and Reopen on a closed
// one, each where it answers. The deal page and the deal peek render the same
// component, so the two cannot offer different verbs for one deal.
//
// WHERE A VERB SHOWS. Every verb needs `sales.opportunity.write` and the
// deal's own `mutable`, which says whether THIS reader's write doors answer
// for the deal (§7 narrowing 16: a sales principal reads a deal a planner
// lineage lets it read, and writes none of them). A verb that would answer
// 404 is not offered (D-088). Reopen follows `reopenOffered`
// (lib/pipeline.ts).
//
// MARK WON ASKS WHAT WON THE DEAL (D-312 ruling 9). The dialog offers the
// customer's converted quotes and confirmed orders the server's win door
// accepts, and "no document" for a win on the expected value
// (`WinDealModal`). The accepted-quote notice hands its quote in as
// `winWith`, and the dialog then opens with that quote already named and no
// picker, so the writer confirms without choosing.
//
// A MOVE HERE IS NOT OPTIMISTIC. The board lays a move over its columns
// because the card is under the reader's hand; here the deal is a header,
// and the honest picture is the server's answer. The request goes, the deal
// reads again, and a refusal says why in one line with its request id.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { newIdempotencyKey } from "../../../../../lib/request";
import type { DropTarget } from "../../lib/board-state";
import { reopenOffered, stageName, type DealStatus } from "../../lib/pipeline";
import { canWriteOpportunities, useSalesPermissions } from "../../hooks/permissions";
import {
  useLoseOpportunity,
  useMoveOpportunity,
  useReopenOpportunity,
  useWinOpportunity,
  type PipelineStage,
} from "../../hooks/opportunity-queries";
import {
  LoseDealModal,
  MoveDealModal,
  RefusalLine,
  WinDealModal,
  type WinWithQuote,
} from "./deal-dialogs";

export type { WinWithQuote } from "./deal-dialogs";

export interface ActionableDeal {
  readonly id: string;
  readonly title: string;
  /** The customer whose converted quotes and orders Mark won offers. */
  readonly customer_id: string;
  readonly status: DealStatus;
  readonly stage_id: string;
  readonly mutable: boolean;
  readonly won_via: "manual" | "quote_accepted" | null;
  readonly won_order_cancelled: boolean | null;
}

export function DealActions({
  deal,
  stages,
  testPrefix,
  winWith,
  onWinWithHandled,
}: {
  readonly deal: ActionableDeal;
  readonly stages: readonly PipelineStage[];
  /** `sales-deal` on the page, `sales-peek-deal` in the peek. */
  readonly testPrefix: string;
  /** Set by the accepted-quote notice's Mark won with the quote. */
  readonly winWith?: WinWithQuote | undefined;
  readonly onWinWithHandled?: (() => void) | undefined;
}) {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const move = useMoveOpportunity();
  const win = useWinOpportunity();
  const lose = useLoseOpportunity();
  const reopen = useReopenOpportunity();
  const [dialog, setDialog] = useState<"move" | "win" | "lose" | null>(null);

  const canWrite = canWriteOpportunities(perms) && deal.mutable;
  const nameOf = (id: string) => {
    const stage = stages.find((s) => s.id === id);
    return stage === undefined ? undefined : stageName(stage, i18n.language);
  };
  const resetAll = () => {
    move.reset();
    win.reset();
    lose.reset();
    reopen.reset();
  };

  const winOpen = dialog === "win" || winWith !== undefined;
  const closeWin = () => {
    setDialog(null);
    onWinWithHandled?.();
  };

  const choose = (target: DropTarget) => {
    setDialog(null);
    if (target.kind === "won") {
      win.reset();
      setDialog("win");
      return;
    }
    if (target.kind === "lost") {
      lose.reset();
      setDialog("lose");
      return;
    }
    move.mutate({
      id: deal.id,
      body: { stage_id: target.stageId, from_stage_id: deal.stage_id },
      idempotencyKey: newIdempotencyKey(),
    });
  };

  if (!canWrite) return null;

  return (
    <div className="sales-deal-actions" data-testid={`${testPrefix}-actions`}>
      <div className="sales-toolbar">
        {deal.status === "open" ? (
          <>
            <Button
              size="sm"
              data-testid={`${testPrefix}-move`}
              busy={move.isPending}
              busyLabel={t("sales.pipeline.move.moving")}
              onClick={() => {
                resetAll();
                setDialog("move");
              }}
            >
              {t("sales.pipeline.deal.move")}
            </Button>
            <Button
              size="sm"
              data-testid={`${testPrefix}-win`}
              onClick={() => {
                resetAll();
                setDialog("win");
              }}
            >
              {t("sales.pipeline.deal.markWon")}
            </Button>
            <Button
              size="sm"
              variant="danger"
              data-testid={`${testPrefix}-lose`}
              onClick={() => {
                resetAll();
                setDialog("lose");
              }}
            >
              {t("sales.pipeline.deal.markLost")}
            </Button>
          </>
        ) : reopenOffered(deal) ? (
          <Button
            size="sm"
            data-testid={`${testPrefix}-reopen`}
            busy={reopen.isPending}
            busyLabel={t("sales.pipeline.deal.reopening")}
            onClick={() => {
              resetAll();
              reopen.mutate(deal.id);
            }}
          >
            {t("sales.pipeline.deal.reopen")}
          </Button>
        ) : null}
      </div>
      {move.isError ? (
        <RefusalLine
          error={move.error}
          fallback={t("sales.pipeline.move.failed")}
          stageNameOf={nameOf}
          testId={`${testPrefix}-move-failed`}
        />
      ) : null}
      {reopen.isError ? (
        <RefusalLine
          error={reopen.error}
          fallback={t("sales.pipeline.deal.reopenFailed")}
          testId={`${testPrefix}-reopen-failed`}
        />
      ) : null}
      {dialog === "move" ? (
        <MoveDealModal
          title={deal.title}
          currentStageId={deal.stage_id}
          stages={stages}
          onClose={() => {
            setDialog(null);
          }}
          onChoose={choose}
        />
      ) : null}
      {winOpen ? (
        <WinDealModal
          title={deal.title}
          dealId={deal.id}
          customerId={deal.customer_id}
          named={winWith}
          pending={win.isPending}
          error={win.error}
          onClose={closeWin}
          onConfirm={(body) => {
            win.mutate({ id: deal.id, body }, { onSuccess: closeWin });
          }}
        />
      ) : null}
      {dialog === "lose" ? (
        <LoseDealModal
          dealId={deal.id}
          title={deal.title}
          pending={lose.isPending}
          error={lose.error}
          onClose={() => {
            setDialog(null);
          }}
          onConfirm={(choice) => {
            lose.mutate(
              {
                id: deal.id,
                body: {
                  reason_code: choice.reason,
                  ...(choice.note !== undefined ? { note: choice.note } : {}),
                },
              },
              {
                onSuccess: () => {
                  setDialog(null);
                },
              },
            );
          }}
        />
      ) : null}
    </div>
  );
}
